/**
 * AuditDigestPanel — Admin → Audit Digest tab.
 *
 * Lets Web Admin:
 *   • toggle the daily digest on/off
 *   • set recipients (comma-separated emails)
 *   • pick a sensitivity preset (verbose | balanced | silent_unless_critical)
 *   • preview the digest for any look-back window (default 24h)
 *   • send a real test email NOW
 *
 * Backend routes: GET/PUT /api/audit-digest/config, GET /preview, POST /send-now.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Loader2, Send, Eye, Save, ShieldAlert, MailCheck, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const SENSITIVITY_DESCRIPTIONS = {
  verbose: "All anomalies, even low-severity informational events.",
  balanced: "High & medium severity only (recommended).",
  silent_unless_critical: "High severity only. Email is NOT sent if there are zero critical events.",
};

const DOW_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SEVERITY_PILL = {
  high: "bg-rose-100 text-rose-800 border-rose-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

export default function AuditDigestPanel({ api }) {
  const [config, setConfig] = useState(null);
  const [draftRecipients, setDraftRecipients] = useState("");
  const [draftSensitivity, setDraftSensitivity] = useState("balanced");
  const [draftEnabled, setDraftEnabled] = useState(true);
  // Weekly state (V23.0.1)
  const [draftWeeklyEnabled, setDraftWeeklyEnabled] = useState(true);
  const [draftWeeklyRecipients, setDraftWeeklyRecipients] = useState("");
  const [draftWeeklySensitivity, setDraftWeeklySensitivity] = useState("balanced");
  const [draftWeeklyDow, setDraftWeeklyDow] = useState("0");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {report, subject, kind}

  const loadConfig = async () => {
    try {
      const res = await api.get("/audit-digest/config");
      const c = res.data?.config || {};
      setConfig(c);
      setDraftRecipients((c.recipients || []).join(", "));
      setDraftSensitivity(c.sensitivity || "balanced");
      setDraftEnabled(c.enabled !== false);
      setDraftWeeklyEnabled(c.weekly_enabled !== false);
      setDraftWeeklyRecipients((c.weekly_recipients || []).join(", "));
      setDraftWeeklySensitivity(c.weekly_sensitivity || "balanced");
      setDraftWeeklyDow(String(c.weekly_dow ?? 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load digest config");
    }
  };

  useEffect(() => { loadConfig(); /* eslint-disable-next-line */ }, []);

  const recipientsList = () =>
    draftRecipients.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const weeklyRecipientsList = () =>
    draftWeeklyRecipients.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/audit-digest/config", {
        enabled: draftEnabled,
        recipients: recipientsList(),
        sensitivity: draftSensitivity,
        weekly_enabled: draftWeeklyEnabled,
        weekly_recipients: weeklyRecipientsList(),
        weekly_sensitivity: draftWeeklySensitivity,
        weekly_dow: parseInt(draftWeeklyDow, 10),
      });
      toast.success("Digest settings saved");
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = async (kind = "daily") => {
    setPreviewing(true);
    try {
      const params = kind === "weekly"
        ? { kind: "weekly", sensitivity: draftWeeklySensitivity }
        : { hours: 24, sensitivity: draftSensitivity };
      const res = await api.get("/audit-digest/preview", { params });
      setPreviewData({ ...res.data, kind });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const sendNow = async (kind = "daily") => {
    const list = kind === "weekly" ? weeklyRecipientsList() : recipientsList();
    if (list.length === 0) {
      toast.error(`Add at least one ${kind} recipient first`);
      return;
    }
    const setBusy = kind === "weekly" ? setSendingWeekly : setSending;
    setBusy(true);
    try {
      const res = await api.post("/audit-digest/send-now", {
        recipients_override: list,
        sensitivity_override: kind === "weekly" ? draftWeeklySensitivity : draftSensitivity,
        hours: kind === "weekly" ? 168 : 24,
        kind,
      });
      const d = res.data || {};
      if (d.sent) {
        toast.success(`${kind === "weekly" ? "Weekly" : "Daily"} digest sent to ${d.delivered}/${d.recipient_count} (${d.anomaly_count} anomalies)`);
      } else if (d.reason === "silent_no_critical") {
        toast.info("Silent mode: no critical anomalies — email not sent.");
      } else {
        toast.warning("Send completed with issues — check delivery log");
      }
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <Card data-testid="audit-digest-loading">
        <CardContent className="py-10 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading digest settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
    <Card data-testid="audit-digest-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-violet-600" />
              Daily Audit Anomaly Digest
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              An automated 8:00 AM PT email summarising suspicious activity from the last 24 hours.
              Fires only if <strong>Enabled</strong> and at least one recipient is set.
            </p>
          </div>
          {config.last_run_at && (
            <Badge variant="outline" className="text-[10px]" data-testid="digest-last-run">
              <MailCheck className="w-3 h-3 mr-1" />
              Last run: {new Date(config.last_run_at).toLocaleString()}
              {config.last_result?.delivered != null && (
                <span className="ml-1.5">· delivered {config.last_result.delivered}</span>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Enabled toggle */}
        <div className="flex items-center justify-between p-3 rounded-md border bg-slate-50">
          <div>
            <Label className="text-sm font-semibold">Enabled</Label>
            <p className="text-[11px] text-slate-500">When off, the 8 AM PT scheduler skips the daily send.</p>
          </div>
          <Switch
            checked={draftEnabled}
            onCheckedChange={setDraftEnabled}
            data-testid="digest-enabled-switch"
          />
        </div>

        {/* Recipients */}
        <div>
          <Label htmlFor="digest-recipients" className="text-sm font-semibold">Recipients</Label>
          <p className="text-[11px] text-slate-500 mb-1.5">Comma-separated. Up to 10 addresses.</p>
          <Input
            id="digest-recipients"
            value={draftRecipients}
            onChange={(e) => setDraftRecipients(e.target.value)}
            placeholder="support@resiliencyhub.ai, ops@resiliencyhub.ai"
            data-testid="digest-recipients-input"
            className="font-mono text-xs"
          />
        </div>

        {/* Sensitivity */}
        <div>
          <Label htmlFor="digest-sensitivity" className="text-sm font-semibold">Sensitivity</Label>
          <Select value={draftSensitivity} onValueChange={setDraftSensitivity}>
            <SelectTrigger id="digest-sensitivity" data-testid="digest-sensitivity-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="verbose">Verbose &mdash; all anomalies</SelectItem>
              <SelectItem value="balanced">Balanced &mdash; high &amp; medium (recommended)</SelectItem>
              <SelectItem value="silent_unless_critical">Silent unless critical &mdash; high only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1.5">{SENSITIVITY_DESCRIPTIONS[draftSensitivity]}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
          <Button
            type="button"
            onClick={saveConfig}
            disabled={saving}
            data-testid="digest-save-btn"
            className="h-9"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save settings
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => preview("daily")}
            disabled={previewing}
            data-testid="digest-preview-btn"
            className="h-9"
          >
            {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
            Preview (last 24h)
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => sendNow("daily")}
            disabled={sending || recipientsList().length === 0}
            data-testid="digest-send-now-btn"
            className="h-9"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Send test now
          </Button>
        </div>
      </CardContent>
    </Card>

    {/* ─────────────────────────────────────────────────────────────────────
        Weekly Executive Summary (V23.0.1)
        ──────────────────────────────────────────────────────────────────── */}
    <Card data-testid="weekly-digest-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-sky-600" />
              Weekly Executive Summary
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              A 7-day rollup with week-over-week trends, anomaly themes, and the top 3 most severe events &mdash; sent every Monday at 8 AM PT.
              Designed for leadership recipients who want a security pulse without the daily noise.
            </p>
          </div>
          {config.weekly_last_run_at && (
            <Badge variant="outline" className="text-[10px]" data-testid="weekly-digest-last-run">
              <MailCheck className="w-3 h-3 mr-1" />
              Last run: {new Date(config.weekly_last_run_at).toLocaleString()}
              {config.weekly_last_result?.delivered != null && (
                <span className="ml-1.5">· delivered {config.weekly_last_result.delivered}</span>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Enabled toggle */}
        <div className="flex items-center justify-between p-3 rounded-md border bg-slate-50">
          <div>
            <Label className="text-sm font-semibold">Enabled</Label>
            <p className="text-[11px] text-slate-500">When off, the Monday morning scheduler skips the weekly send.</p>
          </div>
          <Switch
            checked={draftWeeklyEnabled}
            onCheckedChange={setDraftWeeklyEnabled}
            data-testid="weekly-digest-enabled-switch"
          />
        </div>

        {/* Recipients */}
        <div>
          <Label htmlFor="weekly-recipients" className="text-sm font-semibold">Recipients</Label>
          <p className="text-[11px] text-slate-500 mb-1.5">
            Typically execs &amp; leadership. Comma-separated. Up to 10 addresses.
          </p>
          <Input
            id="weekly-recipients"
            value={draftWeeklyRecipients}
            onChange={(e) => setDraftWeeklyRecipients(e.target.value)}
            placeholder="ceo@resiliencyhub.ai, cto@resiliencyhub.ai"
            data-testid="weekly-digest-recipients-input"
            className="font-mono text-xs"
          />
        </div>

        {/* Day of week */}
        <div>
          <Label htmlFor="weekly-dow" className="text-sm font-semibold">Send on</Label>
          <Select value={draftWeeklyDow} onValueChange={setDraftWeeklyDow}>
            <SelectTrigger id="weekly-dow" data-testid="weekly-digest-dow-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOW_LABELS.map((label, idx) => (
                <SelectItem key={idx} value={String(idx)}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1.5">Weekly digest fires at 8:15 AM PT on the selected day.</p>
        </div>

        {/* Sensitivity */}
        <div>
          <Label htmlFor="weekly-sensitivity" className="text-sm font-semibold">Sensitivity</Label>
          <Select value={draftWeeklySensitivity} onValueChange={setDraftWeeklySensitivity}>
            <SelectTrigger id="weekly-sensitivity" data-testid="weekly-digest-sensitivity-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="verbose">Verbose &mdash; all anomalies</SelectItem>
              <SelectItem value="balanced">Balanced &mdash; high &amp; medium (recommended)</SelectItem>
              <SelectItem value="silent_unless_critical">Silent unless critical &mdash; high only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1.5">{SENSITIVITY_DESCRIPTIONS[draftWeeklySensitivity]}</p>
        </div>

        {/* Actions — Save button is shared between daily & weekly cards (state persists together) */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => preview("weekly")}
            disabled={previewing}
            data-testid="weekly-digest-preview-btn"
            className="h-9"
          >
            {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
            Preview (last 7d)
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => sendNow("weekly")}
            disabled={sendingWeekly || weeklyRecipientsList().length === 0}
            data-testid="weekly-digest-send-now-btn"
            className="h-9"
          >
            {sendingWeekly ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Send test now
          </Button>
          <p className="text-[11px] text-slate-500 ml-auto">
            Click <strong>Save settings</strong> in the daily card above to persist changes here too.
          </p>
        </div>
      </CardContent>
    </Card>

      {/* Preview dialog */}
      <Dialog open={!!previewData} onOpenChange={(o) => !o && setPreviewData(null)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="digest-preview-dialog"
          aria-describedby="digest-preview-desc"
        >
          <DialogHeader>
            <DialogTitle className="text-sm">{previewData?.subject || "Digest preview"}</DialogTitle>
            <DialogDescription id="digest-preview-desc" className="text-[11px] text-slate-500">
              Live preview of what would be emailed to recipients for the
              last {previewData?.kind === "weekly" ? "7 days" : "24 hours"}.
            </DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(previewData.report?.summary || {}).map(([k, v]) => (
                  <div key={k} className="p-2 rounded border bg-slate-50">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{k.replace(/_/g, " ")}</div>
                    <div className="text-lg font-bold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5 mt-2">
                  Anomalies ({(previewData.report?.anomalies || []).length})
                </div>
                {(previewData.report?.anomalies || []).length === 0 ? (
                  <div className="p-4 text-center rounded border bg-emerald-50 text-emerald-700">
                    No anomalies detected.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {previewData.report.anomalies.map((a) => (
                      <div key={a.id} className="p-2.5 rounded border" data-testid={`digest-anomaly-${a.type}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${SEVERITY_PILL[a.severity] || ""}`}>
                            {a.severity}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">{a.occurred_at?.slice(0, 19).replace("T", " ")}</span>
                        </div>
                        <div className="font-semibold text-slate-800 text-[12px]">{a.title}</div>
                        <div className="text-slate-600 text-[11px] mt-0.5">{a.description}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
